import type { ContactFilter } from '@deltasuite/zoho-client';
import { useCallback, useEffect, useState } from 'react';
import { isDialApiConfigured } from '../api/client';
import {
  createSavedFilter,
  deleteSavedFilter,
  listSavedFilters,
  updateSavedFilter,
  type SavedFilter,
} from '../api/saved-filters';

// In-memory fallback when the worker isn't reachable from this build (no
// VITE_DIAL_API_URL / VITE_DEV_USER_ID). Mirrors the unique (user_id, name)
// constraint from public.saved_filters by replace-on-name-collision.
const memStore = new Map<string, SavedFilter>();
const memListeners = new Set<() => void>();
function emit() {
  for (const l of memListeners) l();
}
function nowIso() {
  return new Date().toISOString();
}

function memList(): SavedFilter[] {
  return [...memStore.values()].sort((a, b) => a.name.localeCompare(b.name));
}

function memUpsertByName(name: string, row: SavedFilter): SavedFilter {
  for (const [id, r] of memStore) {
    if (r.name === name && r.id !== row.id) memStore.delete(id);
  }
  memStore.set(row.id, row);
  emit();
  return row;
}

function memCreate(input: {
  name: string;
  filter: ContactFilter;
  remember_last?: boolean;
}): SavedFilter {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `f-${Date.now()}`;
  const row: SavedFilter = {
    id,
    name: input.name,
    filter: input.filter,
    remember_last: input.remember_last ?? false,
    last_used_at: null,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  return memUpsertByName(row.name, row);
}

function memUpdate(
  id: string,
  patch: Partial<{
    name: string;
    filter: ContactFilter;
    remember_last: boolean;
    touch_last_used: boolean;
  }>,
): SavedFilter | null {
  const row = memStore.get(id);
  if (!row) return null;
  const next: SavedFilter = {
    ...row,
    name: patch.name ?? row.name,
    filter: patch.filter ?? row.filter,
    remember_last: patch.remember_last ?? row.remember_last,
    last_used_at: patch.touch_last_used ? nowIso() : row.last_used_at,
    updated_at: nowIso(),
  };
  memStore.set(id, next);
  emit();
  return next;
}

function memDelete(id: string) {
  memStore.delete(id);
  emit();
}

export interface SavedFiltersHook {
  rows: readonly SavedFilter[];
  loading: boolean;
  error: string | null;
  create: (input: {
    name: string;
    filter: ContactFilter;
    remember_last?: boolean;
  }) => Promise<void>;
  update: (
    id: string,
    patch: Partial<{
      name: string;
      filter: ContactFilter;
      remember_last: boolean;
      touch_last_used: boolean;
    }>,
  ) => Promise<void>;
  remove: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useSavedFiltersHook(): SavedFiltersHook {
  const remote = isDialApiConfigured;

  // Memory-backed snapshot, populated either from listSavedFilters or memStore.
  const [rows, setRows] = useState<SavedFilter[]>(remote ? [] : memList());
  const [loading, setLoading] = useState(remote);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!remote) {
      setRows(memList());
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await listSavedFilters();
      setRows(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }, [remote]);

  useEffect(() => {
    void refresh();
    if (!remote) {
      const update = () => setRows(memList());
      memListeners.add(update);
      return () => {
        memListeners.delete(update);
      };
    }
    return undefined;
  }, [remote, refresh]);

  const create: SavedFiltersHook['create'] = async (input) => {
    if (!remote) {
      memCreate(input);
      return;
    }
    await createSavedFilter(input);
    await refresh();
  };

  const update: SavedFiltersHook['update'] = async (id, patch) => {
    if (!remote) {
      memUpdate(id, patch);
      return;
    }
    await updateSavedFilter(id, patch);
    await refresh();
  };

  const remove: SavedFiltersHook['remove'] = async (id) => {
    if (!remote) {
      memDelete(id);
      return;
    }
    await deleteSavedFilter(id);
    await refresh();
  };

  return { rows, loading, error, create, update, remove, refresh };
}
